import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import MobileShell from '@/components/MobileShell';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ACCEPTED_AUDIO_FORMATS, validateRingtoneFile } from '@/lib/providerPush';

export default function AdminProviderDispatch() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const audioRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/provider-dispatch');
      setConfig(data.config);
    } catch {
      toast.error('Could not load dispatch settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const preview = async () => {
    if (config?.using_default) {
      toast.message('Using default notification sound');
      return;
    }
    try {
      const { data } = await api.get('/admin/provider-dispatch/ringtone/preview', {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(data);
      if (audioRef.current) {
        audioRef.current.src = url;
        await audioRef.current.play();
      }
    } catch {
      toast.error('Preview failed');
    }
  };

  const upload = async (file) => {
    const maxBytes = config?.max_size_bytes || 2 * 1024 * 1024;
    const check = validateRingtoneFile(file, maxBytes);
    if (!check.ok) {
      toast.error(check.error);
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post('/admin/provider-dispatch/ringtone', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await load();
      toast.success('Ringtone updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async () => {
    try {
      await api.delete('/admin/provider-dispatch/ringtone');
      await load();
      toast.success('Custom ringtone removed — using default');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remove failed');
    }
  };

  return (
    <MobileShell title="Provider Dispatch">
      <div className="px-5 py-5 space-y-4" data-testid="admin-provider-dispatch">
        <p className="text-sm text-slate-600">
          Configure incoming request notifications for providers.
        </p>

        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="resqly-card p-4 space-y-4">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Incoming Request Ringtone
            </div>

            <div className="text-sm space-y-1">
              <div>
                Current ringtone:{' '}
                <span data-testid="ringtone-filename" className="font-medium">
                  {config?.using_default
                    ? 'Default system sound'
                    : config?.original_filename || '—'}
                </span>
              </div>
              {!config?.using_default && config?.format && (
                <div data-testid="ringtone-format">Format: {config.format}</div>
              )}
              {!config?.using_default && config?.uploaded_at && (
                <div data-testid="ringtone-uploaded">Uploaded: {config.uploaded_at}</div>
              )}
            </div>

            <p className="text-xs text-slate-500" data-testid="accepted-formats">
              Accepted formats: {ACCEPTED_AUDIO_FORMATS}
            </p>
            <p className="text-xs text-slate-500" data-testid="max-file-size">
              Maximum file size: {config?.max_size_mb || 2} MB
            </p>

            <audio ref={audioRef} className="hidden" data-testid="ringtone-preview-audio" />

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                data-testid="ringtone-preview-btn"
                onClick={preview}
                disabled={config?.using_default}
              >
                Preview
              </Button>
              <Button
                variant="outline"
                size="sm"
                data-testid="ringtone-replace-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
              >
                Replace
              </Button>
              <Button
                variant="outline"
                size="sm"
                data-testid="ringtone-remove-btn"
                onClick={remove}
                disabled={config?.using_default}
              >
                Remove
              </Button>
            </div>

            <input
              ref={fileRef}
              type="file"
              accept=".mp3,.wav,.m4a,.aac,.ogg,audio/*"
              className="hidden"
              data-testid="ringtone-file-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload(file);
              }}
            />

            <div className="border-t pt-3">
              <div className="text-xs font-semibold text-slate-500 mb-2">Upload new ringtone</div>
              <Button
                data-testid="ringtone-upload-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-full"
              >
                {uploading ? 'Uploading…' : 'Choose Audio File'}
              </Button>
            </div>
          </div>
        )}

        <Link
          to="/admin/services"
          className="text-sm text-blue-700 font-medium"
          data-testid="back-to-services"
        >
          ← Back to Service Operations
        </Link>
      </div>
    </MobileShell>
  );
}
